import { inject, Injectable, Injector } from '@angular/core';
import { placeFloating, type FloatRect, type FloatSide } from '../utils/floating-position.js';

type Position = 'top' | 'bottom';

// Far off-screen parking spot for the hidden tooltip (see hide()).
const PARK = '-10000px';
const FADE_MS = 150;

@Injectable({ providedIn: 'root' })
export class TooltipService {
  private el: HTMLDivElement | null = null;
  private currentAnchor: HTMLElement | null = null;
  private hideTimer: any = null;
  private injector = inject(Injector);
  private tourServiceCache: { isActive: () => boolean } | null = null;
  private outsideTapListenerAdded = false;
  // Rects the tooltip must never cover — AppComponent registers the Twitch player: in
  // Chrome anything painted over the iframe trips the SDK's IntersectionObserver v2
  // visibility check and playback pauses.
  private keepOutProvider: (() => FloatRect[]) | null = null;

  setKeepOutProvider(fn: (() => FloatRect[]) | null): void {
    this.keepOutProvider = fn;
  }

  private ensure(): HTMLDivElement {
    if (this.el) return this.el;
    const el = document.createElement('div');
    el.setAttribute('role', 'tooltip');
    el.style.cssText = [
      'position: fixed',
      'z-index: 9999',
      'pointer-events: none',
      'opacity: 0',
      'transform: translateY(2px)',
      'transition: opacity 150ms ease-out, transform 150ms ease-out',
      'background: rgb(17 24 39)',
      'color: rgb(229 231 235)',
      'font-size: 12px',
      'line-height: 1.4',
      'padding: 6px 10px',
      'border-radius: 6px',
      'border: 1px solid rgb(55 65 81)',
      'max-width: 320px',
      'box-shadow: 0 4px 14px rgba(0,0,0,0.5)',
      'visibility: hidden',
      `top: ${PARK}`,
      `left: ${PARK}`,
      'white-space: normal',
      'word-break: break-word',
    ].join(';');
    document.body.appendChild(el);
    this.el = el;
    return el;
  }

  // Used by TooltipDirective on touch (coarse pointer) to decide whether a
  // tap on THIS anchor should toggle the tooltip closed vs. open it.
  isShownFor(anchor: HTMLElement): boolean {
    return this.currentAnchor === anchor;
  }

  show(text: string, anchor: HTMLElement, position: Position = 'top'): void {
    if (!text) return;
    // Suppress hover-tooltips while the onboarding tour is active — they would
    // pop above the spotlight overlay and confuse the user. Lazy-resolve the
    // tour service to avoid a circular import; cache the getter after first call.
    if (this.isTourActive()) return;
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.ensureOutsideTapListener();
    const el = this.ensure();
    el.textContent = text;
    this.currentAnchor = anchor;

    // Measure after content set
    requestAnimationFrame(() => {
      if (this.currentAnchor !== anchor) return; // anchor changed during raf
      const aRect = anchor.getBoundingClientRect();
      const tRect = el.getBoundingClientRect();
      const order: FloatSide[] = position === 'top'
        ? ['top', 'bottom', 'right', 'left']
        : ['bottom', 'top', 'right', 'left'];
      let keepOut: FloatRect[] = [];
      try { keepOut = this.keepOutProvider?.() ?? []; } catch { keepOut = []; }
      const { left, top } = placeFloating(
        aRect,
        { width: tRect.width, height: tRect.height },
        { width: window.innerWidth, height: window.innerHeight },
        order,
        keepOut,
      );

      el.style.left = `${Math.round(left)}px`;
      el.style.top = `${Math.round(top)}px`;
      el.style.visibility = 'visible';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }

  hide(): void {
    if (!this.el) return;
    const el = this.el;
    el.style.opacity = '0';
    el.style.transform = 'translateY(2px)';
    this.currentAnchor = null;
    // After the fade, park it off-screen: an opacity-0 box left where it was shown would
    // still sit over whatever it covered — don't bet on Chrome's occlusion check ignoring
    // a transparent occluder over the player iframe. show() cancels this timer.
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      if (this.currentAnchor) return;
      el.style.visibility = 'hidden';
      el.style.left = PARK;
      el.style.top = PARK;
    }, FADE_MS);
  }

  // Touch-mode safety net: a tap outside the currently-shown tooltip's anchor
  // (a plain div, a different section of the page — anything that isn't
  // another [appTooltip] whose own click handler already manages the swap)
  // closes it. Registered once, lazily, on first show() — most sessions never
  // show a tooltip on a coarse pointer at all, so this stays unregistered then.
  private ensureOutsideTapListener(): void {
    if (this.outsideTapListenerAdded) return;
    this.outsideTapListenerAdded = true;
    document.addEventListener('click', (event) => {
      if (this.currentAnchor && event.target instanceof Node && !this.currentAnchor.contains(event.target)) {
        this.hide();
      }
    });
  }

  private async isTourActiveAsync(): Promise<boolean> {
    // Lazy load OnboardingTourService to avoid a static circular import
    // (it pulls in TwitchChatStateService → SyncService → ... — keep tooltip light).
    if (!this.tourServiceCache) {
      try {
        const { OnboardingTourService } = await import('./onboarding-tour.service.js');
        const svc = this.injector.get(OnboardingTourService);
        this.tourServiceCache = { isActive: () => svc.isActive() };
      } catch { return false; }
    }
    return this.tourServiceCache.isActive();
  }

  private isTourActive(): boolean {
    // Sync read of cached service (set on first hover → 1-frame delay first time only)
    if (!this.tourServiceCache) {
      // Trigger lazy resolution in background; on this first call return false.
      this.isTourActiveAsync();
      return false;
    }
    try { return this.tourServiceCache.isActive(); } catch { return false; }
  }
}
